import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Tooltip, Chip, Badge } from '@mui/material';
import Typography from '@mui/material/Typography';

import { SidebarNavItemProps } from '../../props';
import { StyledListItemButton } from './styled';

const MenuIcon = ({ item, isSelected, level }: { item: SidebarNavItemProps['item']; isSelected: boolean; level: number }) => {
  const iconSelectedColor = 'white';

  if (item.iconSelected && isSelected) {
    const IconSelected = item.iconSelected;
    return <IconSelected strokeWidth={1.5} size="40px" sx={{ color: iconSelectedColor }} />;
  }

  if (item.icon) {
    const Icon = item.icon;
    return <Icon strokeWidth={1.5} size="40px" sx={{ color: iconSelectedColor }} />;
  }

  return (
    <FiberManualRecordIcon
      sx={{
        width: 40,
        height: 40,
        color: iconSelectedColor,
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );
};

const SidebarNavItem = ({ item, level, isSideBarOpen }: SidebarNavItemProps) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const isSelected = (item.url || item.link) === pathname + search;
  const iconSelectedColor = 'white';

  const handleItemClick = () => {
    if (!item.disabled) {
      navigate(item.url!);
    }
  };

  return (
    <Tooltip title={!isSideBarOpen ? item.title : ''} placement="right" arrow disableHoverListener={isSideBarOpen}>
      <StyledListItemButton
        level={level}
        isSideBarOpen={isSideBarOpen}
        iconSelectedColor={iconSelectedColor}
        disabled={item.disabled}
        disableRipple={!isSideBarOpen}
        selected={isSelected}
        onClick={handleItemClick}
        sx={{ marginTop: 1 }}
      >
        <ListItemIcon
          sx={{
            border: isSelected && !isSideBarOpen ? '1px solid white' : 'none',
            minWidth: 40,
            mr: isSideBarOpen ? 1 : 0,
          }}
        >
          <MenuIcon item={item} isSelected={isSelected} level={level} />
        </ListItemIcon>

        {isSideBarOpen && (
          <ListItemText
            primary={
              <Typography
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                variant="body1"
                sx={{ color: iconSelectedColor }}
                width="100%"
                fontWeight={isSelected ? 'bold' : 'normal'}
              >
                {item.title}
              </Typography>
            }
            sx={{ flex: 'none' }}
            secondary={
              item.caption && (
                <Typography variant="caption" sx={{ color: iconSelectedColor }} display="block" gutterBottom>
                  {item.caption}
                </Typography>
              )
            }
          />
        )}

        {isSideBarOpen && item.chip && (
          <Box sx={{ ml: 'auto' }}>
            <Chip {...item.chip} />
          </Box>
        )}

        {/* Notification Badge */}
        {isSideBarOpen && item.notification && (
          <Box sx={{ ml: 'auto' }}>
            <Badge
              badgeContent={item.notification}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#FF4444',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  minWidth: '20px',
                  height: '20px',
                  borderRadius: '10px',
                },
              }}
            />
          </Box>
        )}
      </StyledListItemButton>
    </Tooltip>
  );
};

export default SidebarNavItem;
